import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { apiFetch, useToast } from '../../App';
import { MessageSquare, Mail, Save, Send, ChevronDown, ChevronUp, Search, X, Check, Loader2, Edit3, Eye } from 'lucide-react';

const CHANNEL_ICONS = { SMS: MessageSquare, EMAIL: Mail };
const EVENT_LABELS = {
  OTP: 'OTP Verification', WELCOME: 'Welcome', BOOKING_CONFIRMED: 'Booking Confirmed',
  BOOKING_REMINDER: 'Booking Reminder', BOOKING_CANCELLED: 'Booking Cancelled',
  BOOKING_COMPLETED: 'Booking Completed', PAYMENT_RECEIVED: 'Payment Received',
};

export default function DenTemplates() {
  const addToast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testForm, setTestForm] = useState({ phone: '', email: '', variables: {} });
  const [testResult, setTestResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const data = await apiFetch('/api/admin/templates');
      setTemplates(data);
    } catch (e) { addToast(e.message, 'error'); }
    setLoading(false);
  };

  const handleEdit = (tmpl) => {
    setEditing(tmpl.id);
    setEditForm({ name: tmpl.name, subject: tmpl.subject || '', body: tmpl.body, msg91FlowId: tmpl.msg91FlowId || '', msg91TemplateId: tmpl.msg91TemplateId || '', active: tmpl.active });
    setTesting(null);
    setTestResult(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/api/admin/templates/${editing}`, { method: 'PUT', body: JSON.stringify(editForm) });
      setTemplates(templates.map(t => t.id === editing ? updated : t));
      setEditing(null);
      addToast('Template saved');
    } catch (e) { addToast(e.message, 'error'); }
    setSaving(false);
  };

  const handleTest = async (tmpl) => {
    setTesting(tmpl.id);
    setTestResult(null);
    const vars = {};
    (tmpl.variables || []).forEach(v => { vars[v] = `[${v}]`; });
    setTestForm({ phone: '', email: '', variables: vars });
  };

  const sendTest = async () => {
    const tmpl = templates.find(t => t.id === testing);
    if (!tmpl) return;
    try {
      const res = await apiFetch(`/api/admin/templates/${testing}/test`, {
        method: 'POST',
        body: JSON.stringify(testForm),
      });
      setTestResult(res);
      if (res.send_result?.success) addToast('Test sent successfully!');
      else addToast(res.send_result?.message || 'Test completed (preview only)', 'warning');
    } catch (e) { addToast(e.message, 'error'); }
  };

  const renderPreview = (tmpl) => {
    let body = tmpl.body;
    (tmpl.variables || []).forEach(v => {
      body = body.replaceAll(`{{${v}}}`, `<span style="background:#FF8C42;color:#fff;padding:1px 4px;border-radius:3px;font-size:12px">{{${v}}}</span>`);
    });
    return body;
  };

  const filtered = templates.filter(t => {
    if (channelFilter && t.channel !== channelFilter) return false;
    if (filter && !t.name.toLowerCase().includes(filter.toLowerCase()) && !t.event.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-petfolk-orange border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="den-templates-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Message Templates</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Manage SMS & Email templates for OTPs, bookings, and notifications</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-secondary)' }} />
            <input
              className="petfolk-input pl-9 text-sm"
              style={{ width: '200px' }}
              placeholder="Search templates..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
              data-testid="template-search"
            />
          </div>
          <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
            {['', 'SMS', 'EMAIL'].map(ch => (
              <button key={ch} onClick={() => setChannelFilter(ch)} data-testid={`filter-channel-${ch || 'all'}`}
                className={`px-3 py-2 text-xs font-semibold ${channelFilter === ch ? 'bg-petfolk-orange text-white' : ''}`}
                style={{ color: channelFilter !== ch ? 'var(--text-secondary)' : undefined }}>
                {ch || 'All'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(tmpl => {
          const Icon = CHANNEL_ICONS[tmpl.channel] || MessageSquare;
          const isEditing = editing === tmpl.id;
          const isTesting = testing === tmpl.id;
          const isPreviewing = preview === tmpl.id;

          return (
            <div key={tmpl.id} data-testid={`template-card-${tmpl.id}`} className="petfolk-card overflow-hidden">
              {/* Header row */}
              <div className="p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tmpl.channel === 'SMS' ? 'bg-green-100 dark:bg-green-900/20' : 'bg-blue-100 dark:bg-blue-900/20'}`}>
                  <Icon size={18} className={tmpl.channel === 'SMS' ? 'text-green-600' : 'text-blue-600'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>{tmpl.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tmpl.active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                      {tmpl.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {tmpl.channel} · {EVENT_LABELS[tmpl.event] || tmpl.event}
                    {tmpl.msg91FlowId && ' · Flow ID set'}
                    {tmpl.msg91TemplateId && ' · Template ID set'}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => setPreview(isPreviewing ? null : tmpl.id)} data-testid={`preview-${tmpl.id}`}
                    className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" title="Preview">
                    <Eye size={16} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                  <button onClick={() => handleTest(tmpl)} data-testid={`test-${tmpl.id}`}
                    className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" title="Test">
                    <Send size={16} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                  <button onClick={() => isEditing ? setEditing(null) : handleEdit(tmpl)} data-testid={`edit-${tmpl.id}`}
                    className={`p-2 rounded-lg ${isEditing ? 'bg-petfolk-orange/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}>
                    <Edit3 size={16} className={isEditing ? 'text-petfolk-orange' : ''} style={{ color: !isEditing ? 'var(--text-secondary)' : undefined }} />
                  </button>
                </div>
              </div>

              {/* Preview panel */}
              {isPreviewing && (
                <div className="px-4 pb-4 animate-fade-in">
                  <div className="rounded-xl p-4 text-sm" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                    {tmpl.subject && <p className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Subject: {tmpl.subject}</p>}
                    {tmpl.channel === 'EMAIL' ? (
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(tmpl)) }} />
                    ) : (
                      <p style={{ color: 'var(--text-primary)' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(tmpl)) }} />
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(tmpl.variables || []).map(v => (
                        <span key={v} className="text-xs px-2 py-0.5 rounded-full font-mono" style={{ background: 'rgba(255,140,66,0.1)', color: '#c2410c' }}>
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Edit panel */}
              {isEditing && (
                <div className="px-4 pb-4 space-y-3 animate-fade-in" data-testid={`edit-form-${tmpl.id}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Template Name</label>
                      <input className="petfolk-input text-sm" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>MSG91 {tmpl.channel === 'SMS' ? 'Flow' : 'Template'} ID</label>
                        <input className="petfolk-input text-sm font-mono" placeholder="Optional" value={tmpl.channel === 'SMS' ? editForm.msg91FlowId : editForm.msg91TemplateId}
                          onChange={e => setEditForm({...editForm, [tmpl.channel === 'SMS' ? 'msg91FlowId' : 'msg91TemplateId']: e.target.value})} />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 pb-2 cursor-pointer">
                          <button onClick={() => setEditForm({...editForm, active: !editForm.active})}
                            className={`w-10 h-6 rounded-full relative transition-colors ${editForm.active ? 'bg-green-500' : ''}`}
                            style={{ background: !editForm.active ? 'var(--border-color)' : undefined }}>
                            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editForm.active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Active</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  {tmpl.channel === 'EMAIL' && (
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Subject</label>
                      <input className="petfolk-input text-sm" value={editForm.subject} onChange={e => setEditForm({...editForm, subject: e.target.value})} />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Body {tmpl.channel === 'EMAIL' ? '(HTML)' : '(Text)'}</label>
                    <textarea className="petfolk-input text-sm font-mono" rows={tmpl.channel === 'EMAIL' ? 8 : 4} value={editForm.body}
                      onChange={e => setEditForm({...editForm, body: e.target.value})} />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Variables:</span>
                    {(tmpl.variables || []).map(v => (
                      <span key={v} className="text-xs px-2 py-0.5 rounded-full font-mono cursor-pointer hover:scale-105 transition-transform"
                        style={{ background: 'rgba(255,140,66,0.1)', color: '#c2410c' }}
                        onClick={() => setEditForm({...editForm, body: editForm.body + `{{${v}}}`})}>
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditing(null)} className="btn-outline text-sm py-2 px-4">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2 px-4" data-testid={`save-${tmpl.id}`}>
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
                    </button>
                  </div>
                </div>
              )}

              {/* Test panel */}
              {isTesting && (
                <div className="px-4 pb-4 space-y-3 animate-fade-in" data-testid={`test-form-${tmpl.id}`}>
                  <div className="rounded-xl p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                    <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Send Test {tmpl.channel}</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      {tmpl.channel === 'SMS' ? (
                        <input className="petfolk-input text-sm" placeholder="Phone (e.g. +919876543210)" value={testForm.phone}
                          onChange={e => setTestForm({...testForm, phone: e.target.value})} data-testid="test-phone" />
                      ) : (
                        <input className="petfolk-input text-sm" placeholder="Email" value={testForm.email}
                          onChange={e => setTestForm({...testForm, email: e.target.value})} data-testid="test-email" />
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 mb-3">
                      {(tmpl.variables || []).map(v => (
                        <div key={v}>
                          <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>{v}</label>
                          <input className="petfolk-input text-sm" value={testForm.variables[v] || ''} placeholder={v}
                            onChange={e => setTestForm({...testForm, variables: {...testForm.variables, [v]: e.target.value}})} />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setTesting(null); setTestResult(null); }} className="btn-outline text-sm py-2 px-3">Close</button>
                      <button onClick={sendTest} className="btn-primary text-sm py-2 px-4" data-testid="send-test-btn">
                        <Send size={14} /> Send Test
                      </button>
                    </div>
                    {testResult && (
                      <div className="mt-3 rounded-lg p-3 text-sm" style={{ background: testResult.send_result?.success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)' }}>
                        <p className="font-semibold text-xs mb-1" style={{ color: testResult.send_result?.success ? '#15803d' : '#b91c1c' }}>
                          {testResult.send_result?.success ? 'Sent successfully!' : testResult.send_result?.message || 'Preview only'}
                        </p>
                        <p className="text-xs font-mono break-all" style={{ color: 'var(--text-secondary)' }}>
                          {tmpl.channel === 'EMAIL' ? testResult.rendered_subject : testResult.rendered_body}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="petfolk-card p-12 text-center">
          <p style={{ color: 'var(--text-secondary)' }}>No templates found</p>
        </div>
      )}
    </div>
  );
}
